import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import {
  BenchmarkRecomputeFieldDelta,
  BenchmarkRecomputePlayer,
  HISTORICAL_SCENARIO,
  commitBenchmarkRecompute,
  fetchBenchmarkCoverageDiagnostics,
  fetchEligibleBenchmarkOptions,
  previewBenchmarkRecompute,
  selectors,
} from 'state';
import { formatters } from 'services';

import CompositionBuilderV2 from '../../../TrainerSession/SessionSettingsDense/benchmark_v2/CompositionBuilderV2';
import BenchmarkLibraryV2 from '../../../TrainerSession/SessionSettingsDense/benchmark_v2/BenchmarkLibraryV2';
import {
  BasketRow,
  LibOption,
  OptionCoverage,
  evenSplit,
  indexByRic,
  round1,
  toLibOptions,
} from '../../../TrainerSession/SessionSettingsDense/benchmark_v2/helpers';
import '../../../TrainerSession/SessionSettingsDense/benchmark_v2/benchmark_v2.css';

const { Text } = Typography;

// Metric fields shown in the recompute preview (label + payload key + axis
// formatter). Formatters mirror the metrics tab's canonical `metricInputSettings[*].
// player_graph_formatter` (MetricsLimits2.tsx): Sharpe/Treynor/Beta → 2-dp commas, Alpha →
// rounded %. The two benchmark-return fields have no limit-metric entry, so they reuse
// `percent1Digit` — the same primitive portfolio/benchmark returns are graphed with.
const PREVIEW_METRICS: {
  key: string;
  label: string;
  format: (v: number) => string;
}[] = [
  { key: 'market_index_return', label: 'Benchmark return', format: v => formatters.percent1Digit(v) },
  { key: 'daily_market_index_return', label: 'Daily benchmark return', format: v => formatters.percent1Digit(v) },
  { key: 'portfolio_return', label: 'Portfolio return', format: v => formatters.percent1Digit(v) },
  { key: 'portfolio_volatility', label: 'Portfolio volatility', format: v => formatters.percent1Digit(v) },
  { key: 'mdd', label: 'Max drawdown', format: v => formatters.percent1Digit(v) },
  { key: 'beta', label: 'Beta', format: v => formatters.commas2Digits(v) },
  { key: 'alpha', label: 'Alpha', format: v => `${Math.round(v * 100)}%` },
  { key: 'sharpe_ratio', label: 'Sharpe', format: v => formatters.commas2Digits(v) },
  { key: 'treynor_ratio', label: 'Treynor', format: v => formatters.commas2Digits(v) },
];

type Mode = 'saved' | 'custom';

interface Props {
  open: boolean;
  onClose: () => void;
  sessionId: number;
}

const fmt = (value: number | null | undefined): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '-';
  }
  return Math.abs(value) < 0.001 && value !== 0 ? value.toExponential(2) : value.toFixed(4);
};

const DeltaTag: React.FC<{ delta?: BenchmarkRecomputeFieldDelta }> = ({ delta }) => {
  if (!delta || delta.delta === null) {
    return <Text type="secondary">-</Text>;
  }
  const color = delta.delta > 0 ? 'green' : delta.delta < 0 ? 'red' : 'default';
  return (
    <Tag color={color}>
      {delta.delta >= 0 ? '+' : ''}
      {fmt(delta.delta)}
    </Tag>
  );
};

// Compact current-vs-proposed overlay for one metric across the covered periods.
const CompareChart: React.FC<{
  label: string;
  dates: string[];
  current: (number | null)[];
  proposed: number[];
  format: (v: number) => string;
}> = ({ label, dates, current, proposed, format }) => {
  const axisFormatter = (v: number) => (Number.isFinite(v) ? format(v) : '-');
  const options: ApexOptions = {
    chart: { type: 'line', height: 170, toolbar: { show: false }, animations: { enabled: false } },
    stroke: { width: 2, dashArray: [5, 0] },
    colors: ['#8c8c8c', '#1677ff'],
    xaxis: { categories: dates.map(d => formatters.dateOnly(d)), labels: { rotate: 0, show: false } },
    yaxis: { labels: { formatter: axisFormatter } },
    title: { text: label, style: { fontSize: '12px', fontWeight: 600 } },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px' },
    tooltip: { shared: true, y: { formatter: axisFormatter } },
  };
  const series = [
    { name: 'current', data: current },
    { name: 'proposed', data: proposed },
  ];
  return <Chart options={options} series={series} type="line" height={170} />;
};

const BenchmarkRecomputeDrawer: React.FC<Props> = ({ open, onClose, sessionId }) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.sessionById(sessionId));

  const [mode, setMode] = useState<Mode>('saved');
  const [basket, setBasket] = useState<BasketRow[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const preview = session?.benchmark_recompute_preview;
  const previewWaiting = session?.benchmark_recompute_preview_waiting;
  const committing = session?.benchmark_metrics_recompute_waiting;

  const isHistorical = session?.scenario_type === HISTORICAL_SCENARIO;
  // RT only: overlay per-fund coverage (from the coverage diagnostics) so under-covered funds render
  // disabled and can't be added -> a picked benchmark is skip-proof over the recompute dates.
  // Historical keeps the eligible-pool gating (already coverage + non-zero gated), so no overlay.
  const coverageByRic = useMemo(() => {
    if (isHistorical) return undefined;
    const diag = session?.benchmark_coverage_diagnostics;
    if (!diag) return undefined;
    const map: Record<string, OptionCoverage> = {};
    [...diag.index_funds, ...diag.equities].forEach(item => {
      map[item.ric] = {
        currently_eligible: item.currently_eligible,
        missing_count: item.missing_count,
      };
    });
    return map;
  }, [isHistorical, session?.benchmark_coverage_diagnostics]);

  const libOptions: LibOption[] = useMemo(
    () => toLibOptions(session?.eligible_benchmark_options, coverageByRic),
    [session?.eligible_benchmark_options, coverageByRic],
  );
  const optionIndex = useMemo(() => indexByRic(libOptions), [libOptions]);
  const selectedRics = useMemo(() => new Set(basket.map(r => r.ric)), [basket]);

  // Load the benchmark option library (admin page may not have it yet) + seed the candidate basket
  // from the session's saved benchmark whenever the drawer opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (!session?.eligible_benchmark_options && !session?.eligible_benchmark_options_waiting) {
      dispatch(fetchEligibleBenchmarkOptions({ session_id: sessionId }));
    }
    // RT: pull coverage diagnostics to gate the picker (under-covered funds shown disabled).
    if (
      session?.scenario_type !== HISTORICAL_SCENARIO &&
      !session?.benchmark_coverage_diagnostics &&
      !session?.benchmark_coverage_diagnostics_waiting
    ) {
      dispatch(fetchBenchmarkCoverageDiagnostics({ session_id: sessionId }));
    }
    setBasket(
      (session?.benchmark_compositions ?? []).map(c => ({
        ric: c.ric ?? String(c.fund),
        weight: round1((c.weight ?? 0) * 100),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  const mutate = useCallback((next: BasketRow[]) => setBasket(next), []);
  const onAdd = useCallback(
    (option: LibOption) => {
      // Defensive: the library already blocks disabled rows; never let a coverage-gated fund in.
      if (option.disabled || selectedRics.has(option.ric)) {
        return;
      }
      mutate([...basket, { ric: option.ric, weight: basket.length === 0 ? 100 : 0 }]);
    },
    [basket, selectedRics, mutate],
  );
  const onRemove = useCallback(
    (ric: string) => mutate(basket.filter(r => r.ric !== ric)),
    [basket, mutate],
  );
  const onWeight = useCallback(
    (ric: string, weight: number | null) => {
      const w = weight ?? 0;
      if (basket.length === 2) {
        const other = round1(Math.max(0, Math.min(100, 100 - w)));
        mutate(basket.map(r => ({ ...r, weight: r.ric === ric ? w : other })));
        return;
      }
      mutate(basket.map(r => (r.ric === ric ? { ...r, weight: w } : r)));
    },
    [basket, mutate],
  );
  const onEvenSplit = useCallback(() => mutate(evenSplit(basket)), [basket, mutate]);

  const candidateSpec = useMemo(
    () => basket.map(r => ({ fund_or_asset_ric: r.ric, weight: r.weight / 100 })),
    [basket],
  );
  const candidateForRequest = mode === 'custom' ? candidateSpec : undefined;

  const runPreview = () =>
    dispatch(
      previewBenchmarkRecompute({ session_id: sessionId, candidate_benchmark: candidateForRequest }),
    );

  const apply = () => {
    dispatch(
      commitBenchmarkRecompute({ session_id: sessionId, candidate_benchmark: candidateForRequest }),
    );
    onClose();
  };

  const uncoveredEntries = Object.entries(preview?.uncovered ?? {});
  const missingSnapshotEntries = Object.entries(preview?.missing_snapshots ?? {});
  const hasBlockingData = uncoveredEntries.length > 0 || missingSnapshotEntries.length > 0;

  const columns = [
    { title: 'Player', dataIndex: 'name', key: 'name' },
    {
      title: 'Coverage',
      key: 'coverage',
      render: (_: unknown, player: BenchmarkRecomputePlayer) => {
        const full = player.covered_count === player.total_count;
        return (
          <Tag color={full ? 'green' : 'orange'}>
            {player.covered_count}/{player.total_count}
          </Tag>
        );
      },
    },
    {
      title: 'Rows',
      key: 'rows',
      render: (_: unknown, player: BenchmarkRecomputePlayer) => (
        <Space size={4}>
          <Tag>{player.updated_count} update</Tag>
          {player.created_count ? <Tag color="blue">+{player.created_count}</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Δ Benchmark',
      key: 'd_bench',
      render: (_: unknown, p: BenchmarkRecomputePlayer) => <DeltaTag delta={p.summary?.market_index_return} />,
    },
    {
      title: 'Δ Sharpe',
      key: 'd_sharpe',
      render: (_: unknown, p: BenchmarkRecomputePlayer) => <DeltaTag delta={p.summary?.sharpe_ratio} />,
    },
    {
      title: 'Δ Alpha',
      key: 'd_alpha',
      render: (_: unknown, p: BenchmarkRecomputePlayer) => <DeltaTag delta={p.summary?.alpha} />,
    },
    {
      title: 'Δ Beta',
      key: 'd_beta',
      render: (_: unknown, p: BenchmarkRecomputePlayer) => <DeltaTag delta={p.summary?.beta} />,
    },
  ];

  const expandedRowRender = (player: BenchmarkRecomputePlayer) => {
    if (player.covered_count === 0) {
      return <Empty description="No covered periods for this benchmark." />;
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {PREVIEW_METRICS.map(metric => (
          <CompareChart
            key={metric.key}
            label={metric.label}
            dates={player.dates}
            current={player.current[metric.key] ?? []}
            proposed={player.proposed[metric.key] ?? []}
            format={metric.format}
          />
        ))}
      </div>
    );
  };

  return (
    <Drawer
      title="Recompute metrics against benchmark"
      width={960}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space>
          <Button type="primary" loading={previewWaiting} onClick={runPreview}>
            Run preview
          </Button>
          <Popconfirm
            title="Apply the recompute?"
            description="This persists the benchmark and rewrites the affected metrics for all players."
            okText="Apply"
            onConfirm={apply}
          >
            <Button danger loading={committing} disabled={!preview || preview.unsupported_mode || hasBlockingData}>
              Apply
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Segmented
            value={mode}
            onChange={value => setMode(value as Mode)}
            options={[
              { label: 'Recompute current benchmark', value: 'saved' },
              { label: 'Try a candidate benchmark', value: 'custom' },
            ]}
          />
        </div>

        {mode === 'custom' ? (
          <div className="bmv2-root">
            <div className="bmv2-comp2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <BenchmarkLibraryV2
                options={libOptions}
                selected={selectedRics}
                waiting={session?.eligible_benchmark_options_waiting}
                onAdd={onAdd}
                onRemove={onRemove}
              />
              <CompositionBuilderV2
                basket={basket}
                optionIndex={optionIndex}
                onWeight={onWeight}
                onRemove={onRemove}
                onEvenSplit={onEvenSplit}
                onSave={runPreview}
                saving={!!previewWaiting}
                dirty
              />
            </div>
          </div>
        ) : null}

        {preview?.unsupported_mode ? (
          <Alert
            type="warning"
            showIcon
            message="Unsupported benchmark"
            description="Only composition benchmarks (index funds / assets) can be recomputed. Constant and portfolio-composition benchmarks are not supported."
          />
        ) : null}

        {uncoveredEntries.length > 0 ? (
          <Alert
            type="error"
            showIcon
            message={`Missing benchmark prices on ${uncoveredEntries.length} date(s)`}
            description={
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                {uncoveredEntries.map(([date, rics]) => (
                  <div key={date}>
                    {formatters.dateOnly(date)}: {rics.join(', ')}
                  </div>
                ))}
              </div>
            }
          />
        ) : null}

        {missingSnapshotEntries.length > 0 ? (
          <Alert
            type="error"
            showIcon
            message={`Missing player history on ${missingSnapshotEntries.length} date(s)`}
            description={
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                {missingSnapshotEntries.map(([date, playerIds]) => (
                  <div key={date}>
                    {formatters.dateOnly(date)}: player {playerIds.join(', ')}
                  </div>
                ))}
              </div>
            }
          />
        ) : null}

        {preview?.var05_deferred ? (
          <Alert
            type="info"
            showIcon
            message="Created rows will use var05 = 0"
            description="var05 backfill is deferred; existing rows keep their current var05."
          />
        ) : null}

        {previewWaiting ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : preview && !preview.unsupported_mode ? (
          preview.players.length ? (
            <Table
              rowKey="player_id"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={preview.players}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: keys => setExpandedRowKeys(keys as number[]),
                expandedRowRender,
              }}
            />
          ) : (
            <Empty description="No utilized players with metrics to recompute." />
          )
        ) : (
          <Text type="secondary">Run a preview to compare current vs proposed metrics.</Text>
        )}
      </Space>
    </Drawer>
  );
};

export default BenchmarkRecomputeDrawer;
